import { Input } from "@/components/ui/input.tsx";
import { type LocationSuggestion, searchPlaces } from "@/lib/places.ts";
import { cn } from "@/lib/utils.ts";
import { MapPin } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

type Props = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  onSelect?: (suggestion: LocationSuggestion) => void;
  placeholder?: string;
  autoFocus?: boolean;
  inputClassName?: string;
};

export const InlineLocationInput = ({
  value,
  onChange,
  onBlur,
  onSelect,
  placeholder,
  autoFocus,
  inputClassName,
}: Props) => {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Pending debounced search timer. Owned locally (rather than via a shared
  // debounce util) so we can cancel it on unmount and on every keystroke.
  const debounceTimerRef = useRef<number | undefined>(undefined);
  // Sequence counter that guards against out-of-order responses: increment on
  // every search kick-off, and ignore any in-flight result whose sequence is
  // older than the latest one.
  const requestSeqRef = useRef(0);

  // Cancel any pending timer on unmount so a delayed search can't fire after
  // the component is gone (which would also setSuggestions on unmounted state).
  useEffect(
    () => () => {
      if (debounceTimerRef.current !== undefined) {
        window.clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  const handleChange = (next: string) => {
    onChange(next);
    if (debounceTimerRef.current !== undefined) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
    const trimmed = next.trim();
    // Clear stale results eagerly when the query drops below the threshold —
    // otherwise the previous suggestions stay visible until the next debounced
    // call fires (up to SEARCH_DEBOUNCE_MS later).
    if (trimmed.length < MIN_QUERY_LENGTH) {
      requestSeqRef.current += 1;
      setSuggestions([]);
      return;
    }
    setOpen(true);
    const seq = ++requestSeqRef.current;
    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = undefined;
      void (async () => {
        try {
          const results = await searchPlaces(trimmed, 5);
          if (seq !== requestSeqRef.current) return;
          setSuggestions(results);
        } catch {
          if (seq !== requestSeqRef.current) return;
          setSuggestions([]);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSelect = (suggestion: LocationSuggestion) => {
    onChange(suggestion.address);
    onSelect?.(suggestion);
    setSuggestions([]);
    setOpen(false);
  };

  // Close the dropdown when clicking outside the wrapper.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <Input
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        onBlur={(e) => {
          // Tab-away (or any keyboard-driven blur) doesn't fire the
          // document `mousedown` listener, so close the dropdown here too.
          // Suggestion clicks call preventDefault on mousedown, so blur
          // never fires while a suggestion is being selected.
          setOpen(false);
          onBlur?.(e.target.value);
        }}
        placeholder={placeholder}
        className={inputClassName}
      />
      {open && suggestions.length > 0 ? (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-72 overflow-auto rounded-md border bg-popover shadow-md py-1">
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              // mousedown fires before the input's blur, so the click
              // registers without the dropdown being torn down first.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(s)}
              className={cn(
                "flex w-full items-start gap-2 px-3 py-2 text-left transition-colors",
                "hover:bg-accent",
              )}
            >
              <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{s.name}</div>
                <div className="truncate text-xs text-muted-foreground">{s.address}</div>
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
